'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useUAMP } from './useUAMP';
import { useAuth } from '@/components/auth-provider';
import type { Notification } from '@/lib/notifications/service';

/**
 * NO SESSION, NO FETCH (round 7, lane 4, item 3). `enabled` alone was the
 * gate, and the one caller mounted on EVERY page, the root
 * `CriticalNotificationOverlay` (app-providers.tsx), passes `true`: so a
 * signed-out `/w/` landing asked `/api/notifications/unread-count` and
 * `/api/notifications?limit=20` on every load and logged two 401s each time
 * (six in the round 6 dev run, 2026-09-27, polish 15). The session is read
 * here, once, so no caller can ask without one, whatever it passes. A person
 * who signs in later still gets the first read: the fetch effect re-runs when
 * `active` turns true. tests/unit/notifications/r7-no-session-no-fetch.test.tsx
 *
 * A FAILED FIRST COUNT IS ASKED FOR AGAIN (2026-10-03). The unread count is
 * read once per page and then only moved by live events: one up for an
 * arrival, one down for a read. So a first read that failed left the count
 * at zero for the life of the page and every arrival counted from there. The
 * visual pass of 2026-10-03 caught it on a database blip (the count route
 * answered 401 after 80 s): the rail chip, the full-screen bar's chip and
 * the menu row all said 1 while the notifications card beside them, which
 * reads its own list, said 54. A deploy's switch-over can produce the same
 * failed first read. The count is now asked for again after 5, 20 and 60
 * seconds, and never more: a session that really has ended costs three
 * extra refusals and then silence.
 * tests/unit/notifications/unread-count-asks-again.test.tsx
 *
 * THE LIST ON OPEN HOLDS EVERYTHING THE COUNT HOLDS (2026-10-12, the
 * production two-account test). Twice the bell said "14 unread" while the
 * panel it opens listed 13 rows on its first open and not the newest
 * ("Volodymyr Seliuchenko would like to meet", "Open Revent to answer."),
 * created seconds to minutes before; closing and reopening the panel showed
 * it. The count and the list are two instances of this hook: the bell's
 * lives for the page and moves its count live over the socket; the panel's
 * mounts when the panel opens and READS the list then. On production that
 * read was answered by the service worker with the copy captured on the
 * page's load, from before the notice existed (public/sw.template.js; the
 * list is now on its never-cache list, which is the fix for what was seen).
 * The same shape is reachable without a worker: a notice that arrives over
 * the socket while the list read is in the air used to be prepended and
 * then OVERWRITTEN by the read's answer when the server had not yet got it
 * in, so the first paint of the list could still be one notice short of the
 * count. A list read now remembers the notices that arrived live while it
 * was in flight and puts them, newest first, in front of what the server
 * answered, once each. tests/unit/notifications/list-open-includes-live-notice.test.tsx
 */

/** How long after a failed read of the unread count each further try waits. */
export const UNREAD_COUNT_RETRY_DELAYS_MS: readonly number[] = [5_000, 20_000, 60_000];

/**
 * The list a fresh read answers, with the notices that arrived live while
 * the read was in the air in front of it, newest first and each once: a
 * notice the server already had in its answer is not doubled, and the
 * server's copy (its row) is the one kept. Exported for the test.
 */
export function mergeLiveNotices(live: readonly Notification[], fetched: readonly Notification[]): Notification[] {
  const seen = new Set(fetched.map((n) => n.id));
  const front: Notification[] = [];
  for (const n of live) {
    if (!n || typeof n.id !== 'string' || seen.has(n.id)) continue;
    seen.add(n.id);
    front.push(n);
  }
  return [...front, ...fetched];
}

export function useNotifications(enabled = true) {
  const { user } = useAuth();
  const active = enabled && !!user;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);
  const { onUAMPEvent } = useUAMP();

  // What a waiting retry checks before it asks: still mounted, still signed
  // in. Refs, because the retry outlives the render that scheduled it. The
  // mount flag is set in the effect and not at declaration: development
  // runs every effect twice (mount, unmount, mount), and a flag cleared by
  // the middle step must come back.
  const aliveRef = useRef(true);
  const activeRef = useRef(active);
  activeRef.current = active;
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    };
  }, []);

  /** Reads the count. True when the server answered with one. */
  const fetchUnreadCount = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch('/api/notifications/unread-count');
      if (!res.ok) return false;
      const data = await res.json();
      setUnreadCount(data.count ?? 0);
      return true;
    } catch {
      return false;
    }
  }, []);

  // The list read in the air, if any, and the notices that arrived live
  // while it was (see the note on the hook). Only a fresh read (no cursor)
  // replaces the list, so only a fresh read keeps one; a page read appends
  // and cannot drop a live notice already at the front.
  const listReadRef = useRef<{ live: Notification[] } | null>(null);

  const fetchNotifications = useCallback(async (opts?: { cursor?: string; type?: string }) => {
    const read = opts?.cursor ? null : { live: [] as Notification[] };
    if (read) listReadRef.current = read;
    try {
      const params = new URLSearchParams();
      if (opts?.cursor) params.set('cursor', opts.cursor);
      if (opts?.type) params.set('type', opts.type);
      params.set('limit', '20');

      const res = await fetch(`/api/notifications?${params}`);
      if (!res.ok) return [];
      const data = await res.json();
      const items: Notification[] = data.notifications ?? [];

      if (opts?.cursor) {
        setNotifications(prev => [...prev, ...items]);
      } else {
        setNotifications(mergeLiveNotices(read?.live ?? [], items));
      }
      return items;
    } catch {
      return [];
    } finally {
      if (read && listReadRef.current === read) listReadRef.current = null;
    }
  }, []);

  const markRead = useCallback(async (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await fetch(`/api/notifications/${notificationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isRead: true }),
        // A row whose link carries a fragment leaves the page with a full
        // navigation (components/notification-item.tsx): without keepalive
        // the unload cancels this request and the row comes back unread.
        keepalive: true,
      });
    } catch { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);

    try {
      await fetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'read_all' }),
      });
    } catch { /* ignore */ }
  }, []);

  const performAction = useCallback(async (
    notificationId: string,
    action: 'approve' | 'deny' | 'always_approve' | 'approve_once',
    duration?: string,
    /**
     * Dollar-denominated auto-approve threshold for the "Set limit"
     * picker. Only consumed when `action === 'always_approve'`; the
     * server clamps it to `>= amount` and falls back to a 5x default
     * when omitted (see lib/notifications/service.ts).
     */
    threshold?: number,
  ) => {
    try {
      const res = await fetch(`/api/notifications/${notificationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, duration, threshold }),
      });
      const data = await res.json();
      if (data.success) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, isRead: true, actionTaken: action === 'deny' ? 'denied' : 'approved' } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      return data;
    } catch {
      return { success: false, error: 'Network error' };
    }
  }, []);

  const performCtaAction = useCallback((notificationId: string, action: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true, actionTaken: action } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    try {
      await fetch(`/api/notifications/${notificationId}`, { method: 'DELETE' });
    } catch { /* ignore */ }
  }, []);

  // Initial fetch: only with a session (see the note on the hook) and only
  // when the caller enabled it.
  useEffect(() => {
    if (!active || fetchedRef.current) return;
    fetchedRef.current = true;
    // The count, and again on a failure (see the note on the hook).
    const ask = (attempt: number) => {
      void fetchUnreadCount().then((ok) => {
        if (ok || !aliveRef.current || !activeRef.current) return;
        const wait = UNREAD_COUNT_RETRY_DELAYS_MS[attempt];
        if (wait === undefined) return;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          if (aliveRef.current && activeRef.current) ask(attempt + 1);
        }, wait);
      });
    };
    ask(0);
    void fetchNotifications().finally(() => setLoading(false));
  }, [active, fetchUnreadCount, fetchNotifications]);

  // Listen for UAMP events (notification.new, notification.read)
  useEffect(() => {
    return onUAMPEvent((event: any) => {
      if (event.type === 'notification.new' && event.notification) {
        // A read in the air will replace the list when it lands: remember
        // the notice so that answer cannot leave it out (the note on the hook).
        listReadRef.current?.live.unshift(event.notification);
        setNotifications(prev => [event.notification, ...prev.filter(n => n.id !== event.notification.id)]);
        if (!event.notification.isRead) {
          setUnreadCount(prev => prev + 1);
        }
      }
      if (event.type === 'notification.read') {
        if (event.all) {
          setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
          setUnreadCount(0);
        } else if (event.notificationId) {
          setNotifications(prev =>
            prev.map(n => n.id === event.notificationId
              ? { ...n, isRead: true, ...(event.actionTaken ? { actionTaken: event.actionTaken } : {}) }
              : n
            )
          );
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    });
  }, [onUAMPEvent]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markAllRead,
    performAction,
    performCtaAction,
    deleteNotification,
  };
}
